import React, { useEffect, useState } from "react";

/**
 * Big color-shifting countdown timer.
 * Blue >30s, Green ≤30s, Red ≤0 (continues counting up as +MM:SS).
 */
export default function CodeTimer({
  title,
  durationSec,
  resetKey,
  onTap,
  subtitle,
  badge,
  icon,
  testid,
  frozen,
}) {
  const [now, setNow] = useState(Date.now());
  const [start, setStart] = useState(Date.now());

  useEffect(() => {
    setStart(Date.now());
  }, [resetKey, durationSec]);

  useEffect(() => {
    if (frozen) return;
    const i = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(i);
  }, [frozen]);

  const elapsedMs = now - start;
  const remainingSec = durationSec - Math.floor(elapsedMs / 1000);
  const overdue = remainingSec < 0;
  const abs = Math.abs(remainingSec);
  const mm = String(Math.floor(abs / 60)).padStart(2, "0");
  const ss = String(abs % 60).padStart(2, "0");
  const display = overdue ? `+${mm}:${ss}` : `${mm}:${ss}`;

  let color = "text-sky-400";
  let glow = "shadow-[0_0_20px_rgba(56,189,248,0.15)]";
  if (overdue) {
    color = "text-red-500";
    glow = "shadow-[0_0_30px_rgba(239,68,68,0.35)]";
  } else if (remainingSec <= 30) {
    color = "text-emerald-400";
    glow = "shadow-[0_0_24px_rgba(52,211,153,0.25)]";
  }

  const overdueLabel = title === "PULSE CHECK" ? "OVERDUE — TAP WHEN DONE" : "OVERDUE — TAP WHEN GIVEN";

  return (
    <button
      onClick={onTap}
      disabled={frozen}
      data-testid={testid}
      className={`relative w-full bg-[#0e1a30] border border-[#1f2c47] rounded-2xl p-4 sm:p-5 text-left transition-all hover:border-[#3a4a6e] active:scale-[0.99] overflow-hidden ${glow} ${frozen ? "opacity-60" : ""}`}
    >
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {icon}
          <span className="text-xs sm:text-sm font-bold tracking-widest text-sky-300 uppercase truncate">{title}</span>
        </div>
        {badge && (
          <span className="font-mono text-[10px] sm:text-xs font-bold tracking-wider text-fuchsia-300 bg-fuchsia-500/10 border border-fuchsia-500/30 px-1.5 py-0.5 rounded shrink-0">
            {badge}
          </span>
        )}
      </div>
      <div
        className={`font-mono font-black tabular-nums tracking-tighter text-center my-1 leading-none ${color}`}
        style={{ fontSize: "clamp(2.25rem, 9vw, 5rem)" }}
      >
        {display}
      </div>
      <div className="text-center text-[10px] sm:text-xs font-semibold tracking-wider uppercase mt-1 truncate">
        {overdue ? (
          <span className="text-red-400">{overdueLabel}</span>
        ) : (
          <span className="text-slate-400">{subtitle}</span>
        )}
      </div>
    </button>
  );
}
